import React, { useState } from 'react';

import Button from '~/components/button';
import InputField from '~/components/inputs/input-field';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import { parseCouponError } from '~/features/cart/cart';
import { useCartMutations } from '~/features/cart/cart-actions';

import styles from '~/components/cart/cart-summary/cart-summary.module.scss';

/**
 * <CouponForm /> can add a coupon to a user's cart. It can
 * be embedded inside other forms (ex: on the checkout page),
 * so it's not a true HTML <form>. Instead it's an input and
 * a button that's wired together to act like a form.
 */
export const CouponForm = ({
  onCouponError,
  addCouponOverride,
  cartUuid,
  onCouponAdd = () => {},
}) => {
  const { showFlashMessage } = useFlashMessage();
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState();
  const { addCoupon } = useCartMutations();

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!code) {
      return;
    }

    onCouponError(null);
    let updatedCart;
    try {
      setIsSubmitting(true);
      if (addCouponOverride) {
        await addCouponOverride(code.toUpperCase(), cartUuid);
      } else {
        updatedCart = await addCoupon(cartUuid, code.toUpperCase());
      }

      showFlashMessage('Coupon applied.', 'success', 3000);
      onCouponAdd(updatedCart);
    } catch (err) {
      const message = parseCouponError(err);
      setIsSubmitting(false);
      onCouponError(message);
    }
  };

  return (
    <div className={styles.couponForm} data-testid="coupon-form">
      <div className={styles.couponInputWrapper}>
        <InputField
          input={{
            className: styles.couponInput,
            style: { marginBottom: 0 },
            placeholder: 'Enter code',
            name: 'code',
            onChange: (e) => {
              setCode(e.target.value.toUpperCase());
            },
            onKeyDown: handleKeyDown,
            value: code,
            autoComplete: 'off',
            'aria-label': 'Coupon code',
          }}
          label={<span className={styles.couponLabel}>Coupon code</span>}
          meta={{}}
        />
      </div>
      <Button
        type="button"
        className={styles.couponSubmitBtn}
        onClick={handleSubmit}
        isLoading={isSubmitting}
      >
        Apply
      </Button>
    </div>
  );
};

export default CouponForm;
